import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';

import { Field, Form, Formik } from 'formik';

import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Input,
  Textarea,
  Flex,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

import { updateCategoryAction } from 'store/modules/category/category-actions';
import { categoryActions } from 'store/modules/category/category-slice';

import { ReducerStatus } from 'utils/constants';

import useFileInput from 'hooks/useFileInput';

import ResultModal from 'components/common/modals/ResultModal';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import ImageUploadInput from 'components/common/forms/ImageUploadInput';
import CategoryImage from 'components/category/CategoryImage';
import CategoryThumbnailInfo from 'components/category/CategoryThumbnailInfo';
import { CloseIcon, RepeatIcon } from '@chakra-ui/icons';

function UpdateCategoryMd() {
  const dispatch = useDispatch();

  const updateCategory = useSelector((state) => state.category.updateCategory);
  const selectedCategory = useSelector((state) => state.category.category);

  const categoryFormValidation = Yup.object().shape({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
  });

  /**
   * Custom hook for handling thumbnail file upload functionality
   * Provides file selection, preview, validation, and removal capabilities
   * Maximum file size: 5MB, File type: image/*
   */
  const {
    file,
    fileURL,
    fileError,
    fileInputRef,
    handleAttachFile,
    handleRemoveSelectedFile,
    handleFileInputChange,
  } = useFileInput({ maxFileSizeMb: 5, fileType: 'image/' });

  const [updateFormInputs, setUpdateFormInputs] = useState({
    name: '',
    description: '',
  });

  /**
   * State to track whether current thumbnail should be removed
   * This will be sent to the server to indicate thumbnail deletion
   */
  const [removeThumbnail, setRemoveThumbnail] = useState(false);

  /**
   * Resets form fields and clears selected thumbnail file
   * Also resets thumbnail removal state
   */
  const resetForm = () => {
    setUpdateFormInputs({
      name: '',
      description: '',
    });
    handleRemoveSelectedFile();
    setRemoveThumbnail(false);
  };

  /**
   * Handles removal of current thumbnail
   * Sets flag to indicate thumbnail should be deleted on server
   */
  const handleRemoveCurrentThumbnail = () => {
    setRemoveThumbnail(true);
  };

  /**
   * Reverts thumbnail removal action
   * Resets the removal flag to show current thumbnail again
   */
  const handleRevertThumbnailRemoval = () => {
    setRemoveThumbnail(false);
  };

  const handleToggleUpdateModal = (isOpen) => {
    dispatch(categoryActions.toggleUpdateModal(isOpen));
  };

  const handleResultButton = () => {
    dispatch(categoryActions.resetUpdateStatus());
    if (updateCategory.status === ReducerStatus.SUCCEEDED) {
      resetForm();
      handleToggleUpdateModal(false);
    }
  };

  /**
   * Handles category update by preparing form data including thumbnail file
   * Creates FormData object to support file upload alongside text fields
   * Also includes thumbnail removal flag if current thumbnail should be deleted
   * @param {Object} formikObject - Form values from Formik validation
   */
  const handleUpdateCategory = (formikObject) => {
    const formData = new FormData();

    // Add form fields to FormData
    Object.entries(formikObject).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add thumbnail file if selected
    if (file && file.name) {
      formData.append('Thumbnail', file, file.name);
    }

    // Add flag to remove current thumbnail if requested
    if (removeThumbnail) {
      formData.append('RemoveThumbnail', 'true');
    }

    dispatch(
      updateCategoryAction({
        id: selectedCategory.id,
        formData,
      })
    );
  };

  useEffect(() => {
    if (updateCategory.modal) {
      setUpdateFormInputs({
        name: selectedCategory?.name || '',
        description: selectedCategory?.description || '',
      });
      setRemoveThumbnail(false); // Reset thumbnail removal state
    } else {
      dispatch(categoryActions.resetUpdateStatus());
    }
  }, [updateCategory.modal, selectedCategory, dispatch]);

  return (
    <Box>
      {updateCategory.status === ReducerStatus.SUCCEEDED ||
      updateCategory.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateCategory.modal}
          isError={updateCategory.status === ReducerStatus.FAILED}
          onSubmit={handleResultButton}
          title={
            updateCategory.status === ReducerStatus.SUCCEEDED
              ? 'Category Updated'
              : null
          }
          message={
            updateCategory.status === ReducerStatus.SUCCEEDED
              ? 'The category has been updated successfully.'
              : updateCategory.error?.data?.title || 'Server Error'
          }
          errorObject={updateCategory.error?.data?.errors}
        />
      ) : (
        <Modal
          size="lg"
          isOpen={updateCategory.modal}
          onClose={() => handleToggleUpdateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Category</ModalHeader>
            <ModalCloseButton />
            <Formik
              enableReinitialize
              initialValues={updateFormInputs}
              onSubmit={(values) => {
                handleUpdateCategory(values);
              }}
              validationSchema={categoryFormValidation}
            >
              {() => (
                <Form>
                  <ModalBody my={3}>
                    <VStack spacing={6} align="stretch">
                      <Field flex={1} name="name">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Name"
                            helperText="Enter the name of the category. Make it concise and descriptive."
                            errors={form.errors.name}
                            form={form}
                            touched={form.touched.name}
                          >
                            <Input {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <Field name="description">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Description"
                            helperText="Provide a detailed description of the category. Mention the key objectives and what users can expect."
                            errors={form.errors.description}
                            touched={form.touched.description}
                          >
                            <Textarea {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <ImageUploadInput
                        label="Thumbnail"
                        helperText="Upload a category thumbnail (128px icon recommended). Use simple flat icons from flaticon.com with light background and grayscale/muted tones. PNG format preferred. Max 5MB."
                        currentImage={selectedCategory?.thumbnailPath}
                        file={file}
                        fileURL={fileURL}
                        fileError={fileError}
                        fileInputRef={fileInputRef}
                        handleRemoveSelectedFile={handleRemoveSelectedFile}
                        handleAttachFile={handleAttachFile}
                        handleFileInputChange={handleFileInputChange}
                        hideImagePreview
                      >
                        {/* Current Thumbnail Display - only show if no new file is attached and not removed */}
                        {selectedCategory?.thumbnailPath &&
                          !fileURL &&
                          !removeThumbnail && (
                            <Box mb={4}>
                              <Flex
                                justify="space-between"
                                align="center"
                                mb={2}
                              >
                                <Box
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color="gray.700"
                                >
                                  Current Thumbnail
                                </Box>
                                <IconButton
                                  aria-label="Remove current thumbnail"
                                  icon={<CloseIcon />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={handleRemoveCurrentThumbnail}
                                />
                              </Flex>
                              <Box
                                w="100%"
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                overflow="hidden"
                              >
                                <CategoryImage
                                  name={selectedCategory.name}
                                  image={selectedCategory.thumbnailPath}
                                />
                              </Box>
                              <CategoryThumbnailInfo variant="update" />
                            </Box>
                          )}

                        {/* Thumbnail Removal Indicator */}
                        {selectedCategory?.thumbnailPath &&
                          removeThumbnail &&
                          !fileURL && (
                            <Alert status="warning" mb={4} borderRadius="md">
                              <AlertIcon />
                              <Box flex="1">
                                <AlertTitle fontSize="sm">
                                  Thumbnail will be removed!
                                </AlertTitle>
                                <AlertDescription fontSize="xs">
                                  The current category thumbnail will be deleted
                                  when you save changes.
                                </AlertDescription>
                              </Box>
                              <Button
                                size="sm"
                                leftIcon={<RepeatIcon />}
                                colorScheme="orange"
                                variant="outline"
                                onClick={handleRevertThumbnailRemoval}
                              >
                                Undo
                              </Button>
                            </Alert>
                          )}

                        {/* Attached File Preview */}
                        {fileURL && (
                          <Box mb={4}>
                            <Box
                              w="100%"
                              border="1px"
                              borderColor="gray.200"
                              borderRadius="md"
                              overflow="hidden"
                            >
                              <CategoryImage
                                name="New category thumbnail preview"
                                image={fileURL}
                              />
                            </Box>
                            <CategoryThumbnailInfo variant="new" />
                          </Box>
                        )}
                      </ImageUploadInput>
                    </VStack>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      mr={3}
                      onClick={() => handleToggleUpdateModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      colorScheme="red"
                      px={10}
                      type="submit"
                      isLoading={
                        updateCategory.status === ReducerStatus.LOADING
                      }
                    >
                      Update
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default UpdateCategoryMd;
